import { useEffect, useState } from 'react';
import { Sidebar } from './Sidebar';
import { Topbar } from './Topbar';
import { OnboardingTour } from './OnboardingTour';
import { useSidebar } from '@/contexts/SidebarContext';
import { useBreakpoint } from '@/hooks/useBreakpoint';

const SIDEBAR_W = 248;
const SIDEBAR_COLLAPSED_W = 68;

interface AppShellProps {
  title: string;
  children: React.ReactNode;
  noPadding?: boolean;
}


function AppShellInner({ children, noPadding }: { children: React.ReactNode; noPadding?: boolean }): JSX.Element {
  const { collapsed } = useSidebar();
  const bp = useBreakpoint();
  const isMobile = bp === 'mobile';
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  // Close drawer when resizing to desktop
  useEffect(() => {
    if (!isMobile) setMobileNavOpen(false);
  }, [isMobile]);

  // On mobile the sidebar is an overlay — it does not push content
  const sidebarWidth = isMobile ? 0 : collapsed ? SIDEBAR_COLLAPSED_W : SIDEBAR_W;

  return (
    <div style={{ background: 'var(--color-bg)', minHeight: '100vh' }}>
      <Sidebar
        width={isMobile ? SIDEBAR_W : sidebarWidth}
        collapsedWidth={SIDEBAR_COLLAPSED_W}
        isMobile={isMobile}
        mobileNavOpen={mobileNavOpen}
        onMobileClose={() => setMobileNavOpen(false)}
      />

      {/* Mobile backdrop */}
      {isMobile && mobileNavOpen && (
        <div
          className="fixed inset-0 z-40"
          style={{ background: 'rgba(0,0,0,0.5)' }}
          onClick={() => setMobileNavOpen(false)}
        />
      )}

      <Topbar
        sidebarWidth={sidebarWidth}
        isMobile={isMobile}
        onHamburgerClick={() => setMobileNavOpen(o => !o)}
      />

      <main
        style={{
          marginLeft: sidebarWidth,
          paddingTop: 'var(--topbar-height)',
          minHeight: '100vh',
          transition: isMobile ? undefined : 'margin-left 0.22s cubic-bezier(0.4, 0, 0.2, 1)',
          display: noPadding ? 'flex' : undefined,
          flexDirection: noPadding ? 'column' : undefined,
        }}
      >
        {noPadding ? (
          children
        ) : (
          <div style={{ padding: isMobile ? '20px 16px' : '28px 24px', maxWidth: 1800, width: '100%', margin: '0 auto' }}>
            <div className="flex flex-col gap-6">
              {children}
            </div>
          </div>
        )}
      </main>

      <OnboardingTour />
    </div>
  );
}

export function AppShell({ title: _title, children, noPadding }: AppShellProps): JSX.Element {
  return <AppShellInner noPadding={noPadding}>{children}</AppShellInner>;
}
